// Screen Wake Lock helper.
// Keeps the display on during the AR session; silent fallback on unsupported platforms.
// Do NOT call releaseWakeLock from visibility-change handlers — let the browser
// suspend the lock automatically when the tab is backgrounded and reacquire on return.

import { logger } from './logger';

let wakeLock: WakeLockSentinel | null = null;

/**
 * Requests a screen wake lock. Silent no-op if the API is absent or denied.
 * Automatically reacquires the lock when the page becomes visible again after
 * backgrounding (browsers release wake locks on visibility change).
 */
export async function acquireWakeLock(): Promise<void> {
  if (!('wakeLock' in navigator)) return;
  try {
    wakeLock = await navigator.wakeLock.request('screen');
    logger.log('Screen wake lock acquired');

    // Browsers release the wake lock when the tab is hidden.
    // Reacquire it when the page becomes visible again.
    wakeLock.addEventListener('release', () => {
      logger.log('Wake lock released by browser');
    });

    document.addEventListener('visibilitychange', onVisibilityChange);
  } catch (err) {
    logger.log('Screen wake lock unavailable:', err);
  }
}

async function onVisibilityChange(): Promise<void> {
  if (document.visibilityState === 'visible' && !wakeLock) {
    try {
      wakeLock = await navigator.wakeLock.request('screen');
      logger.log('Screen wake lock reacquired');
    } catch {
      // Silently ignore — not critical.
    }
  } else {
    wakeLock = null;
  }
}

/** Releases the wake lock and removes the reacquire listener. */
export function releaseWakeLock(): void {
  document.removeEventListener('visibilitychange', onVisibilityChange);
  if (wakeLock) {
    wakeLock.release().catch(() => {});
    wakeLock = null;
    logger.log('Screen wake lock released');
  }
}
